/**
 * Auth Transition Logger
 *
 * Provides structured logging for auth state transitions to help diagnose
 * when page errors are incorrectly masquerading as logouts.
 *
 * Tracks:
 * - last_route: The route the user was on before the transition
 * - last_user_action: The last meaningful user interaction
 * - redirect_reason: Why the redirect to /auth occurred
 */

import { logErrorTelemetry } from "@/hooks/useErrorTelemetry";
import { logger } from "@/lib/logger";

const STORAGE_KEYS = {
  LAST_ROUTE: "auth_last_route",
  LAST_USER_ACTION: "auth_last_user_action",
  LAST_USER_ACTION_TIME: "auth_last_user_action_time",
} as const;

export type AuthRedirectReason =
  | "session_expired"
  | "token_refresh_failed"
  | "401_response"
  | "403_forbidden"
  | "manual_logout"
  | "not_authenticated"
  | "session_revoked"
  | "unknown";

export type AuthTransitionContext = {
  lastRoute: string | null;
  lastUserAction: string | null;
  lastUserActionTime: string | null;
  redirectReason: AuthRedirectReason;
  currentRoute: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
};

/**
 * Store the current route for later reference in auth transitions
 */
export function trackRoute(route: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.LAST_ROUTE, route);
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

/**
 * Track the last user action (navigation, form submit, button click, etc.)
 */
export function trackUserAction(action: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.LAST_USER_ACTION, action);
    sessionStorage.setItem(STORAGE_KEYS.LAST_USER_ACTION_TIME, new Date().toISOString());
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

/**
 * Get the stored route context
 */
export function getStoredRouteContext(): {
  lastRoute: string | null;
  lastUserAction: string | null;
  lastUserActionTime: string | null;
} {
  try {
    return {
      lastRoute: sessionStorage.getItem(STORAGE_KEYS.LAST_ROUTE),
      lastUserAction: sessionStorage.getItem(STORAGE_KEYS.LAST_USER_ACTION),
      lastUserActionTime: sessionStorage.getItem(STORAGE_KEYS.LAST_USER_ACTION_TIME),
    };
  } catch {
    return {
      lastRoute: null,
      lastUserAction: null,
      lastUserActionTime: null,
    };
  }
}

/**
 * Reasons that don't need telemetry — the redirect is the expected outcome,
 * not a signal worth investigating. Logged to console only.
 *
 * The original purpose of this telemetry was "diagnose when page errors are
 * incorrectly masquerading as logouts" (see file header). That use case wants
 * the *unexpected* redirects (401/session_expired/token_refresh_failed); the
 * benign cases below were polluting error_telemetry with one row per unauth
 * page load.
 */
const BENIGN_REDIRECT_REASONS = new Set<AuthRedirectReason>([
  "not_authenticated",
  "manual_logout",
]);

/**
 * Log an auth transition event for debugging and telemetry
 */
export function logAuthTransition(
  redirectReason: AuthRedirectReason,
  metadata?: Record<string, unknown>
): void {
  const storedContext = getStoredRouteContext();
  const currentRoute = typeof window !== "undefined"
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : "unknown";

  const context: AuthTransitionContext = {
    lastRoute: storedContext.lastRoute,
    lastUserAction: storedContext.lastUserAction,
    lastUserActionTime: storedContext.lastUserActionTime,
    redirectReason,
    currentRoute,
    timestamp: new Date().toISOString(),
    metadata,
  };

  // Log to console for immediate debugging — every transition, regardless of reason.
  logger.info("[AuthTransition]", context);

  // Log to telemetry only for reasons worth investigating. Skipping the benign
  // cases avoids hitting log_client_error on every unauth visitor's boot
  // (~2 round-trips × every pre-login load).
  if (BENIGN_REDIRECT_REASONS.has(redirectReason)) {
    return;
  }

  logErrorTelemetry({
    errorMessage: `[AuthTransition] Redirect to /auth: ${redirectReason}`,
    errorName: "AuthTransition",
    route: storedContext.lastRoute ?? currentRoute,
    metadata: {
      ...context,
      type: "auth_transition",
    },
  });
}

/**
 * Clear stored auth context (call after successful login)
 */
export function clearAuthTransitionContext(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.LAST_ROUTE);
    sessionStorage.removeItem(STORAGE_KEYS.LAST_USER_ACTION);
    sessionStorage.removeItem(STORAGE_KEYS.LAST_USER_ACTION_TIME);
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Map HTTP status codes to auth redirect reasons
 */
export function statusToRedirectReason(status: number | undefined): AuthRedirectReason {
  if (status === 401) return "401_response";
  if (status === 403) return "403_forbidden";
  return "unknown";
}

/**
 * Check if an error indicates an auth issue vs a page/server error
 */
export function isAuthError(error: unknown): boolean {
  if (!error) return false;

  const err = error as { status?: number; code?: string; message?: string };

  // HTTP 401/403 are auth errors
  if (err.status === 401 || err.status === 403) {
    return true;
  }

  // Check for auth-related error messages
  const authPatterns = [
    "jwt",
    "token",
    "unauthorized",
    "forbidden",
    "not authenticated",
    "session expired",
    "session_revoked",
    "invalid refresh token",
  ];

  const message = err.message?.toLowerCase() ?? "";
  return authPatterns.some((pattern) => message.includes(pattern));
}

/**
 * Check if an error indicates a page/server error (should NOT redirect to auth)
 */
export function isPageError(error: unknown): boolean {
  if (!error) return false;

  const err = error as { status?: number; code?: string; message?: string };

  // HTTP 5xx are server errors
  if (err.status && err.status >= 500) {
    return true;
  }

  // JS runtime errors that aren't auth-related
  if (error instanceof TypeError || error instanceof ReferenceError) {
    return !isAuthError(error);
  }

  // Check for common page error patterns
  const pageErrorPatterns = [
    "chunk",
    "module",
    "render",
    "component",
    "cannot read",
    "undefined",
    "null",
  ];

  const message = err.message?.toLowerCase() ?? "";
  return pageErrorPatterns.some((pattern) => message.includes(pattern)) && !isAuthError(error);
}
